import React, { type JSX } from 'react'
import { Observable } from 'rx'
import type { Campaign } from '@mobi/account/Areas/SignUp'
import type { BettingType } from '@mobi/betslip/types'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { observeImmutable } from '../../../../Components/HOCs'
import { QuickbetState, state$ as quickbetState$ } from '../../driver'
import {
  ContainerStyled,
  MysteryQuickPickBonusToggleStyled,
} from './MysteryQuickPickBonusToggleButton.styles'

export type MysteryQuickPickBonusToggleButtonStateProps = {
  isUsingBonusBet: boolean
  isUsingBonusCash: boolean
  isBusy: boolean
  canConfirmBet: boolean
  activeCampaigns: Campaign[] | undefined
  presetInvestment: boolean
  bettingType: BettingType | null
}

export type MysteryQuickPickBonusToggleButtonExternalProps = {
  handleClick: () => void
}

const mysteryQuickPickState = Observable.combineLatest(
  userAccountState$,
  quickbetState$,
  (userAccountState, quickbetState): MysteryQuickPickBonusToggleButtonStateProps => {
    const quickbetStateValue = quickbetState.toJS() as QuickbetState
    return {
      isUsingBonusBet: quickbetStateValue.isUsingBonusBet,
      isUsingBonusCash: quickbetStateValue.isUsingBonusCash,
      isBusy: quickbetStateValue.isBusy,
      canConfirmBet: quickbetStateValue.canConfirmBet,
      activeCampaigns: userAccountState.activeCampaigns
        ?.filter(x => x.rewardType == 'BonusCashReward')
        .toArray(),
      presetInvestment: quickbetStateValue.presetInvestment,
      bettingType: quickbetStateValue.bettingType,
    }
  }
)

export const MysteryQuickPickBonusToggleButtonComponent = ({
  activeCampaigns,
  bettingType,
  presetInvestment,
  canConfirmBet,
  isUsingBonusBet,
  isUsingBonusCash,
  handleClick,
  isBusy,
}: MysteryQuickPickBonusToggleButtonStateProps &
  MysteryQuickPickBonusToggleButtonExternalProps): JSX.Element | null => {
  const hasActiveBonusCashCampaigns = activeCampaigns?.some(
    campaign => campaign.rewardType === 'BonusCashReward'
  )

  if (
    bettingType !== 'mystery-quick-pick' ||
    !presetInvestment ||
    canConfirmBet ||
    // isUsingBonusBet means we see the bonus cash (argh), isUsingBonusCash means we have selected bonus cash to actually use it
    isUsingBonusCash ||
    !hasActiveBonusCashCampaigns
  ) {
    return null
  }

  return (
    <ContainerStyled>
      <MysteryQuickPickBonusToggleStyled
        id='quickbet-bonuscash-button'
        onClick={handleClick}
        selected={isUsingBonusBet}
        disabled={isBusy}
      >
        {isUsingBonusBet ? 'Use Balance' : 'Use My Bonus'}
      </MysteryQuickPickBonusToggleStyled>
    </ContainerStyled>
  )
}

export const MysteryQuickPickBonusToggleButton = observeImmutable<
  MysteryQuickPickBonusToggleButtonStateProps,
  MysteryQuickPickBonusToggleButtonExternalProps
>(mysteryQuickPickState, ({ record, handleClick }) => (
  <MysteryQuickPickBonusToggleButtonComponent {...record} handleClick={handleClick} />
))
